/**
 * Saved-dashboard bridge (WS-D D5). Connects React auth + store to the legacy
 * Save button and auto-loads the user's board on sign-in; renders only the
 * "Loading your board" placeholder while that load is in flight.
 *
 * - **Save:** publishes `window.bentoBoards.save()` (only while signed in) — the
 *   legacy `#save-btn` / `#mobile-save-btn` in `main.js` call it. It serializes
 *   the live store modules → `PUT /boards/default`.
 * - **Load:** when a user signs in, fetch `GET /boards/default` and hydrate the
 *   store via `setModules` (which `<Workspace>` renders). Loads **once per
 *   sign-in** — guarded by userId so a token refresh (a new `user` object, same
 *   identity) never clobbers unsaved in-session edits.
 *
 * Only meaningful on a Cognito-configured build; when signed out there is no
 * `window.bentoBoards`, so the Save button keeps its legacy flash (see main.js).
 */

import { useEffect, useRef, useState } from "react";
import { useAuth } from "./contexts/AuthContext.js";
import { useBentoStore } from "./store/index.js";
import type { BentoModule } from "./store/module.js";
import { ApiError } from "./data/api.js";
import { loadBoard, saveBoard, DEFAULT_BOARD_ID } from "./data/boards.js";
import BoardLoading from "./components/BoardLoading.js";
import { isTitleAllowed } from "./lib/deployment.js";

// Hand the loaded board (or [] for none) to the shell, which applies it now or
// — while the intro is up or the tour is running — once the user has made their
// choice / the tour has closed. Without the shell (tests), apply directly.
// A module this link doesn't serve is skipped (v1's allow-list may shrink; a
// skipped module would only 404), the rest of the board loads as saved — except
// the League Table's season, which always opens on the current season (Ben):
// looking back is a glance, not a setting. (Boards saved before Q-003 also hold
// the old toggle's only option, "2025/26", which would otherwise stick.)
function showBoard(saved: BentoModule[], savedOnMobile: boolean) {
  const modules = saved
    .filter((m) => isTitleAllowed(m.title))
    .map((m) =>
      m.title === "League Table" && m.controls?.season != null
        ? { ...m, controls: { ...m.controls, season: null } }
        : m
    );
  if (window.bentoShell?.onBoardLoaded) window.bentoShell.onBoardLoaded(modules, savedOnMobile);
  else useBentoStore.getState().setModules(modules);
}

export default function BoardsBridge() {
  const { user, loading: authLoading } = useAuth();
  // The userId we've already auto-loaded a board for this session, so a token
  // refresh doesn't reload (and wipe unsaved edits).
  const loadedFor = useRef<string | null>(null);
  // The board is on its way → "Loading your board" on the empty canvas.
  const [loading, setLoading] = useState(false);
  const canvasEmpty = useBentoStore((s) => s.modules.length === 0);

  useEffect(() => {
    // Signed out → no save bridge (legacy flash), reset the load guard, and
    // CLEAR the canvas. The store is one in-memory singleton, so without this a
    // same-tab account switch (sign out → sign in as someone else) would leave
    // the previous user's board on screen — it looks like a tenancy leak even
    // though the server scopes correctly. (On the initial unconfigured/loading
    // pass the store is already empty, so this is a no-op there.)
    if (!user) {
      loadedFor.current = null;
      delete window.bentoBoards;
      useBentoStore.getState().setModules([]);
      return;
    }

    // Publish the save for the legacy Save button (reads the live store).
    window.bentoBoards = {
      save: () =>
        saveBoard({
          modules: useBentoStore.getState().modules,
          savedOnMobile: window.innerWidth <= 768,
        }).then(() => undefined),
    };

    // Auto-load once per sign-in.
    if (loadedFor.current === user.userId) return;
    loadedFor.current = user.userId;

    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const board = await loadBoard(DEFAULT_BOARD_ID);
        if (cancelled) return;
        const modules = Array.isArray(board.modules)
          ? (board.modules as BentoModule[])
          : [];
        showBoard(modules, board.savedOnMobile === true);
      } catch (err) {
        if (cancelled) return;
        // No board (404) or any load failure → start from a CLEAN canvas, so a
        // previous user's board can never show for this user. 404 is the normal
        // "no board yet" case (not logged); other failures are logged.
        showBoard([], false);
        if (!(err instanceof ApiError && err.status === 404)) {
          console.error("[boards] auto-load failed", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [user]);

  // Still checking sign-in, or signed in with the board not yet asked for
  // (the effect below runs after this render) → keep the loader up too, so it
  // doesn't blink off between the two.
  const pending = authLoading || (!!user && loadedFor.current !== user.userId);
  return <BoardLoading show={canvasEmpty && (pending || loading)} />;
}
